import React, { useCallback, useEffect, useRef, useState } from 'react';
import './VariantCropModal.css';

/**
 * Three-step cropper for an activity image. The user must frame the image in the
 * three required display variants — twoThirds (2:3), square (1:1) and half (1:2)
 * — by panning and zooming. onComplete reports { twoThirds, square, half }, each
 * a crop { x, y, width, height } in pixels of the (already stored) source image,
 * which is exactly the ImageVariants body the backend expects. The full image is
 * cropped at display time, so no re-upload is needed.
 *
 * No cropping dependency — plain pointer events + a little math, same approach as
 * AvatarCropModal but with a per-variant rectangular viewport.
 */

const SQUARE = 300; // on-screen size for the square variant (px)
const BANNER_H = 320; // on-screen height for the banner variants (px)
const BANNER_MAX_W = 640; // cap so very wide banners still fit the modal

const VARIANTS = [
  { key: 'twoThirds', label: '2/3 de pantalla', aspect: 3 / 2, banner: true },
  { key: 'square', label: 'Cuadrada', aspect: 1, banner: false },
  { key: 'half', label: '1/2 de pantalla', aspect: 2 / 1, banner: true },
  { key: 'fullWidth', label: 'Ancho completo', aspect: 10 / 3, banner: true },
];

// Banner variants are 320px tall (width follows the aspect), capped so the very
// wide ones still fit; the square fits a fixed box. Only the ratio matters for
// the reported crop — the on-screen size is cosmetic.
const viewportFor = (v) => {
  if (!v.banner) return { w: SQUARE, h: SQUARE };
  let h = BANNER_H;
  let w = Math.round(h * v.aspect);
  if (w > BANNER_MAX_W) {
    w = BANNER_MAX_W;
    h = Math.round(w / v.aspect);
  }
  return { w, h };
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export default function VariantCropModal({ src, title, onCancel, onComplete, saving }) {
  const [img, setImg] = useState(null); // HTMLImageElement
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const [crops, setCrops] = useState({}); // key -> CropRect
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // image top-left in viewport
  const drag = useRef(null);

  const variant = VARIANTS[step];
  const vp = viewportFor(variant);

  // Smallest scale that fully covers the current viewport ("cover").
  const cover = useCallback(
    (z = 1) =>
      img ? Math.max(vp.w / img.naturalWidth, vp.h / img.naturalHeight) * z : 1,
    [img, vp.w, vp.h]
  );

  // Decode the source once.
  useEffect(() => {
    let alive = true;
    const image = new Image();
    image.onload = () => alive && setImg(image);
    image.onerror = () =>
      alive && setError('No hemos podido cargar la imagen para recortarla.');
    image.src = src;
    return () => {
      alive = false;
    };
  }, [src]);

  // Center the image whenever the image loads or the variant (viewport) changes.
  useEffect(() => {
    if (!img) return;
    const s = cover(1);
    setZoom(1);
    setOffset({
      x: (vp.w - img.naturalWidth * s) / 2,
      y: (vp.h - img.naturalHeight * s) / 2,
    });
  }, [img, step, cover, vp.w, vp.h]);

  // Keep the image covering the viewport (no empty gaps) for the current zoom.
  const clampOffset = useCallback(
    (o, z) => {
      if (!img) return o;
      const s = cover(z);
      return {
        x: clamp(o.x, vp.w - img.naturalWidth * s, 0),
        y: clamp(o.y, vp.h - img.naturalHeight * s, 0),
      };
    },
    [img, cover, vp.w, vp.h]
  );

  const onZoom = (e) => {
    const z = Number(e.target.value);
    setZoom(z);
    setOffset((o) => clampOffset(o, z));
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setOffset(
      clampOffset(
        {
          x: drag.current.ox + (e.clientX - drag.current.startX),
          y: drag.current.oy + (e.clientY - drag.current.startY),
        },
        zoom
      )
    );
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  // Crop rect (in source pixels) currently framed by the viewport.
  const currentCrop = () => {
    const s = cover(zoom);
    const x = Math.round(-offset.x / s);
    const y = Math.round(-offset.y / s);
    return {
      x,
      y,
      width: Math.min(Math.round(vp.w / s), img.naturalWidth - x),
      height: Math.min(Math.round(vp.h / s), img.naturalHeight - y),
    };
  };

  const handleNext = () => {
    const next = { ...crops, [variant.key]: currentCrop() };
    setCrops(next);
    if (step < VARIANTS.length - 1) {
      setStep(step + 1);
    } else {
      onComplete(next);
    }
  };

  const s = cover(zoom);

  return (
    <div className="Modal" role="presentation" onMouseDown={saving ? undefined : onCancel}>
      <div
        className="Modal__card VariantCrop"
        role="dialog"
        aria-modal="true"
        aria-label="Recortar variantes"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="Modal__title">{title || 'Ajusta la imagen'}</h2>
        <p className="Modal__desc">
          Variante {step + 1} de {VARIANTS.length}: <strong>{variant.label}</strong>.
          Arrastra y usa el zoom para elegir qué parte se verá.
        </p>
        {error && <p className="Modal__error">{error}</p>}

        <div className="VariantCrop__stage">
          <div
            className="VariantCrop__viewport"
            style={{ width: vp.w, height: vp.h }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {img && (
              <img
                src={img.src}
                alt=""
                draggable={false}
                style={{
                  position: 'absolute',
                  left: offset.x,
                  top: offset.y,
                  width: img.naturalWidth * s,
                  height: img.naturalHeight * s,
                  userSelect: 'none',
                }}
              />
            )}
          </div>
        </div>

        <input
          className="VariantCrop__zoom"
          type="range"
          min="1"
          max="3"
          step="0.01"
          value={zoom}
          onChange={onZoom}
          aria-label="Zoom"
          disabled={!img}
        />

        <div className="VariantCrop__dots">
          {VARIANTS.map((v, i) => (
            <span
              key={v.key}
              className={`VariantCrop__dot${i === step ? ' is-active' : ''}${
                i < step ? ' is-done' : ''
              }`}
            />
          ))}
        </div>

        <div className="Modal__actions">
          <button type="button" className="Btn Btn--soft" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button
            type="button"
            className="Btn Btn--primary"
            onClick={handleNext}
            disabled={!img || saving}
          >
            {saving
              ? 'Guardando…'
              : step < VARIANTS.length - 1
              ? 'Siguiente →'
              : 'Guardar variantes'}
          </button>
        </div>
      </div>
    </div>
  );
}
